(function () {
  'use strict';

  /* ------------------------------------------------------------------
     Line splitter — for `[data-reveal-lines]`: wraps each visual line
     in <span class="editorial-line"><span class="editorial-line__inner">
     so the inner can translate up from behind the mask.
     Re-runs on resize because line breaks depend on container width.
     ------------------------------------------------------------------ */
  function splitLines(el) {
    if (el._origHTML == null) {
      el._origHTML = el.innerHTML;
    } else {
      el.innerHTML = el._origHTML;
    }

    var blocks = el.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6');
    if (!blocks.length) blocks = [el];

    var lineIndex = 0;
    Array.prototype.forEach.call(blocks, function (block) {
      var text = block.textContent;
      if (!text || !text.trim()) return;
      var tokens = text.split(/(\s+)/).filter(function (w) { return w.length > 0; });

      block.innerHTML = '';
      var wordEls = [];
      tokens.forEach(function (w) {
        if (/^\s+$/.test(w)) {
          block.appendChild(document.createTextNode(' '));
        } else {
          var s = document.createElement('span');
          s.style.display = 'inline-block';
          s.textContent = w;
          block.appendChild(s);
          wordEls.push(s);
        }
      });

      // Group word spans by offsetTop into visual lines.
      var lines = [];
      var current = null;
      wordEls.forEach(function (w) {
        if (current === null || Math.abs(w.offsetTop - current.top) > 2) {
          current = { top: w.offsetTop, words: [] };
          lines.push(current);
        }
        current.words.push(w.textContent);
      });

      // Rebuild block: each line in its own mask.
      block.innerHTML = '';
      lines.forEach(function (line) {
        var mask = document.createElement('span');
        mask.className = 'editorial-line';
        var inner = document.createElement('span');
        inner.className = 'editorial-line__inner';
        inner.style.setProperty('--line-delay', (lineIndex * 80) + 'ms');
        inner.textContent = line.words.join(' ');
        mask.appendChild(inner);
        block.appendChild(mask);
        lineIndex++;
      });
    });
  }

  function setupLineReveals() {
    var els = document.querySelectorAll('[data-reveal-lines]');
    if (!els.length) return [];
    Array.prototype.forEach.call(els, splitLines);

    /* Only re-split when the WIDTH changes. Mobile browsers fire `resize` when
       the URL bar shows/hides during scroll (height-only) — re-splitting then
       rebuilds DOM mid-scroll and causes stutter, so we ignore those. */
    var resizeTimer;
    var lastWidth = window.innerWidth;
    window.addEventListener('resize', function () {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        Array.prototype.forEach.call(els, function (el) {
          var wasRevealed = el.classList.contains('is-revealed');
          splitLines(el);
          if (wasRevealed) el.classList.add('is-revealed');
        });
      }, 200);
    });

    return Array.prototype.slice.call(els);
  }

  /* ------------------------------------------------------------------
     Scroll reveal — observe `[data-reveal]` AND `[data-reveal-lines]`.
     ------------------------------------------------------------------ */
  function initReveal() {
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* Only split paragraphs into line-masks when motion is allowed. The mask
       styling (display:block) lives in a no-preference media query, so splitting
       under reduced-motion would leave the masks inline and drop the spaces
       between them (merging words). Leaving the text untouched avoids that. */
    if (!reduce) setupLineReveals();

    var reveals = document.querySelectorAll('[data-reveal], [data-reveal-lines]');
    if (!reveals.length) return;

    if (reduce || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(reveals, function (el) { el.classList.add('is-revealed'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -33% 0px', threshold: 0 });

    Array.prototype.forEach.call(reveals, function (el) { io.observe(el); });

    /* Bottom-of-page safety net for items the trigger can't reach. */
    var bottomTicking = false;
    function revealRemaining() {
      bottomTicking = false;
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) {
        Array.prototype.forEach.call(reveals, function (el) {
          if (!el.classList.contains('is-revealed')) {
            el.classList.add('is-revealed');
            io.unobserve(el);
          }
        });
        window.removeEventListener('scroll', onBottomScroll);
        window.removeEventListener('resize', onBottomScroll);
      }
    }
    function onBottomScroll() {
      if (!bottomTicking) {
        bottomTicking = true;
        window.requestAnimationFrame(revealRemaining);
      }
    }
    window.addEventListener('scroll', onBottomScroll, { passive: true });
    window.addEventListener('resize', onBottomScroll, { passive: true });
    revealRemaining();
  }

  /* ------------------------------------------------------------------
     Intro crossfade (mobile) — the intro section is pinned and its first
     paragraph is replaced by the second as you scroll through, reversing
     on scroll up. Driven by scroll progress through the tall section.
     ------------------------------------------------------------------ */
  function initIntroCrossfade() {
    var sections = document.querySelectorAll('[data-intro]');
    if (!sections.length) return;

    var mqMobile = window.matchMedia('(max-width: 767px)');
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    Array.prototype.forEach.call(sections, function (sec) {
      var swap = sec.querySelector('[data-intro-swap]');
      if (!swap) return;
      var ticking = false;

      function update() {
        ticking = false;
        if (!mqMobile.matches || reduce) { swap.classList.remove('is-second'); return; }
        var rect = sec.getBoundingClientRect();
        var total = sec.offsetHeight - window.innerHeight;
        if (total <= 0) { swap.classList.remove('is-second'); return; }
        var progress = -rect.top / total;
        swap.classList.toggle('is-second', progress > 0.45);
      }
      function onScroll() {
        if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();
    });
  }

  /* ------------------------------------------------------------------
     Slideshow — crossfades `.editorial-slideshow__slide` images on a
     fixed interval. Only runs while in view (perf) and only when motion
     is allowed; under reduced motion the first slide stays put.
     ------------------------------------------------------------------ */
  function initSlideshows() {
    var shows = document.querySelectorAll('[data-editorial-slideshow]');
    if (!shows.length) return;

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;

    Array.prototype.forEach.call(shows, function (show) {
      var slides = show.querySelectorAll('.editorial-slideshow__slide');
      if (slides.length < 2) return;

      var interval = parseInt(show.getAttribute('data-interval'), 10);
      if (!interval || interval < 1000) interval = 5000;

      var index = 0;
      var timer = null;

      function advance() {
        slides[index].classList.remove('is-active');
        index = (index + 1) % slides.length;
        slides[index].classList.add('is-active');
      }
      function start() { if (!timer) timer = window.setInterval(advance, interval); }
      function stop() { if (timer) { window.clearInterval(timer); timer = null; } }

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) start();
            else stop();
          });
        }, { threshold: 0.15 });
        io.observe(show);
      } else {
        start();
      }
    });
  }

  /* ------------------------------------------------------------------
     Image parallax — a taller image inside a shorter fixed-height frame
     drifts upward as the element scrolls through the viewport.
     ------------------------------------------------------------------ */
  function initImageParallax() {
    var figs = document.querySelectorAll('[data-editorial-parallax]');
    if (!figs.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    Array.prototype.forEach.call(figs, function (fig) {
      var img = fig.querySelector('.editorial-image__img');
      if (!img) return;

      /* Continuous rAF loop with easing (lerp) so motion is smooth on mobile
         where scroll events fire coarsely (the source of the stutter). */
      var current = 0, target = 0, running = false, inView = true;

      function computeTarget() {
        var rect = fig.getBoundingClientRect();
        var vh = window.innerHeight;
        var total = vh + rect.height;
        var progress = (vh - rect.top) / total;
        if (progress < 0) progress = 0;
        if (progress > 1) progress = 1;
        target = -progress * (fig.offsetHeight * 0.45); /* matches the image's 150% height */
        inView = !(rect.bottom < -80 || rect.top > vh + 80);
      }
      function loop() {
        computeTarget();
        current += (target - current) * 0.12;
        img.style.transform = 'translate3d(0,' + current.toFixed(2) + 'px,0)';
        if (inView || Math.abs(target - current) > 0.3) {
          window.requestAnimationFrame(loop);
        } else {
          running = false;
        }
      }
      function start() {
        if (!running) { running = true; window.requestAnimationFrame(loop); }
      }
      window.addEventListener('scroll', start, { passive: true });
      window.addEventListener('resize', start, { passive: true });
      start();
    });
  }

  /* ------------------------------------------------------------------
     Videos — play only while on screen. The markup autoplays, but a page
     with many looping videos taxes mobile CPU/battery and can stutter
     scrolling, so we pause anything out of view and resume on entry.
     ------------------------------------------------------------------ */
  function initVideos() {
    var vids = document.querySelectorAll('video[data-editorial-video]');
    if (!vids.length) return;

    /* Honor reduced-motion (WCAG 2.3 / 2.2.2): don't autoplay; show the paused
       first frame / poster instead of looping motion. */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      Array.prototype.forEach.call(vids, function (v) {
        v.removeAttribute('autoplay');
        v.removeAttribute('loop');
        try { v.pause(); } catch (e) {}
      });
      return;
    }

    if (!('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        if (entry.isIntersecting) {
          if (v.paused) {
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
          }
        } else if (!v.paused) {
          v.pause();
        }
      });
    }, { rootMargin: '150px 0px', threshold: 0.1 });

    Array.prototype.forEach.call(vids, function (v) { io.observe(v); });
  }

  function initAll() {
    initReveal();
    initIntroCrossfade();
    initSlideshows();
    initImageParallax();
    initVideos();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
